import React, {
  ReactNode,
  useState,
  createContext,
  useContext,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { useEthWalletApi } from "../hooks/walletConnector/eth/useEthWalletApi";
import {
  ChainDetailType,
  DepositToken,
  WalletDetailType,
  WithdrawToken,
} from "../interfaces/wallet";
import environment from "../environment";

interface UnifiedWalletProviderProps {
  children: ReactNode;
}

interface UnifiedContextType {
  chain: ChainDetailType.Item;
  setChain: (chain: ChainDetailType.Item) => void;
  walletApi: WalletDetailType.WalletAPI;
  disconnectEth: () => Promise<void>;

  reconnect: (newChain: ChainDetailType.Item) => Promise<void>;
}

const getChainFromLocalStorage = (): ChainDetailType.Item => {
  const chain = localStorage.getItem("chain");
  if (chain && chain !== "undefined") {
    return JSON.parse(chain);
  }
  return environment.allowToLoginChains[0];
};

const UnifiedContext = createContext<UnifiedContextType | null>(null);

const UnifiedWalletProvider = ({ children }: UnifiedWalletProviderProps) => {
  const [chain, setChain] = useState<ChainDetailType.Item>(
    getChainFromLocalStorage()
  );
  const [tokenAddress, setTokenAddress] = useState<
    DepositToken | WithdrawToken | undefined
  >(undefined);
  const ethWallet = useEthWalletApi(chain, tokenAddress, setTokenAddress);

  // select wallet api based on chain
  const walletApi = useMemo(() => {
    switch (chain?.chainId) {
      case 56789:
      case 97:
      case 56:
        return ethWallet;
      default:
        return ethWallet;
    }
  }, [chain?.chainId, ethWallet]);

  const reconnect = useCallback(async (newChain: ChainDetailType.Item) => {
    // Reconnect disabled - users must manually connect wallets
    console.warn(
      "Auto-reconnect disabled. Users must manually connect wallets after chain changes."
    );
  }, []);

  const handleSetChain = useCallback((newChain: ChainDetailType.Item) => {
    localStorage.setItem("chain", JSON.stringify(newChain));
    setChain(newChain);
  }, []);

  // fetch balance by useEffect
  const doFetchBalance = useCallback(async () => {
    if (walletApi.isConnected) {
      try {
        await walletApi.fetchBalance();
      } catch (error: any) {
        console.warn(
          "Failed to fetch wallet balance:",
          error?.message || error
        );

        // Check if this is a persistent connection error that should trigger disconnect
        const isPersistentError =
          error?.code === -32603 ||
          error?.code === -32000 ||
          error?.code === "UNKNOWN_ERROR" ||
          error?.message?.includes("could not coalesce error") ||
          error?.message?.includes("Failed to connect") ||
          error?.message?.includes("missing trie node") ||
          error?.message?.includes("Internal JSON-RPC error");

        if (isPersistentError) {
          console.warn(
            "Persistent error in balance fetch, triggering wallet disconnect"
          );

          // Show notification via custom event (will be caught by GlobalErrorHandler)
          window.dispatchEvent(
            new CustomEvent("app-error", {
              detail: {
                message:
                  "Wallet connection error. Wallet disconnected. Please reconnect your wallet",
                context: "Balance Fetch Error",
                isWalletError: true,
              },
            })
          );

          // Also trigger wallet disconnect event for backward compatibility
          window.dispatchEvent(
            new CustomEvent("wallet-disconnect-required", {
              detail: {
                error,
                reason: "Persistent error during balance fetch",
              },
            })
          );
        }
        // Continue silently - balance fetch failures shouldn't break the app
      }
    }
  }, [walletApi]);

  useEffect(() => {
    if (!tokenAddress) return;
    doFetchBalance();
  }, [walletApi, tokenAddress, doFetchBalance]);

  // Add effect to listen for wallet disconnect events
  useEffect(() => {
    const handleWalletDisconnectRequired = async (event: Event) => {
      const customEvent = event as CustomEvent;
      console.warn(
        "Wallet disconnect required due to:",
        customEvent.detail?.reason
      );
      console.warn("Error details:", customEvent.detail?.error);

      try {
        // Disconnect ETH wallet if connected
        if (walletApi.isConnected && walletApi === ethWallet) {
          console.log("Disconnecting ETH wallet due to RPC error");
          await ethWallet.disconnect();
        }

        // Clear any cached wallet data
        localStorage.removeItem("walletName");
        localStorage.removeItem("latestPublicKey");

        console.log(
          "Wallet disconnected successfully. Please reconnect manually."
        );
      } catch (disconnectError) {
        console.warn("Error during wallet disconnect:", disconnectError);
      }
    };

    window.addEventListener(
      "wallet-disconnect-required",
      handleWalletDisconnectRequired
    );

    return () => {
      window.removeEventListener(
        "wallet-disconnect-required",
        handleWalletDisconnectRequired
      );
    };
  }, [walletApi, ethWallet]);

  const value = useMemo(
    () => ({
      chain,
      setChain: handleSetChain,
      walletApi,
      disconnectEth: ethWallet.disconnect,
      reconnect,
    }),
    [chain, handleSetChain, walletApi, ethWallet.disconnect, reconnect]
  );

  return (
    <UnifiedContext.Provider value={value}>{children}</UnifiedContext.Provider>
  );
};

const useUnifiedWallet = () => {
  const context = useContext(UnifiedContext);
  if (context === null) {
    throw new Error("useWallet must be used within a UnifiedWalletProvider");
  }
  return context;
};

export { UnifiedWalletProvider, useUnifiedWallet };
